import { contactStrings } from '@data/contacts';
import { optionalEmailField, requiredStringField, stringField } from '@utils/forms';
import { createValidate, formStrings } from '@utils/forms';
import { validateEnglish } from '@utils/types';
import * as t from 'io-ts';
import { isEqual, pick } from 'lodash-es';
import * as h from 'tyrann-io';

export const contactType = t.union([
  t.literal('consignee'),
  t.literal('pickup_address'),
  t.literal('receiver'),
  t.literal('shipper'),
]);

export type ContactType = t.TypeOf<typeof contactType>;

export const contactBase = t.type({
  company_name: t.string,
  english_name: t.string,
  zip_code: t.string,
  address1: t.string,
  address1_cn: t.string,
  address2: t.string,
  principal_name: t.string,
  phone: t.string,
  email: t.string,
  sns_id: t.string,
  contact_type: contactType,
  social_credit_id: t.string,
});

export type ContactBase = t.TypeOf<typeof contactBase>;

export const contact = t.type({
  ...contactBase.props,
  owner: h.omittable(t.number),
  id: t.number,
  use_count: t.number,
  updated_at: t.string,
  created_at: t.string,
  user: h.omittable(t.number),
  company_id: h.omittable(t.number),
});

export type Contact = t.TypeOf<typeof contact>;

export const patchContact = t.partial({
  ...contactBase.props,
  owner: h.omittable(t.number),
  related_contacts: t.array(t.number),
});

export type PatchContact = t.TypeOf<typeof patchContact>;

export const postContact = t.type({
  ...contactBase.props,
  owner: h.omittable(t.number),
  related_contacts: t.array(t.number),
});

export type PostContact = t.TypeOf<typeof postContact>;

export const contactQuery = t.partial({
  contact_type: contactType,
  ordering: t.string,
  company_name: t.string,
  principal_name: t.string,
  related_to: h.number().cast(),
  sort_by_related_to: h.number().cast(),
  owner_id: h.number().cast(),
  creator_id: h.number().cast(),
  is_user: t.boolean,
});

export type ContactQuery = t.TypeOf<typeof contactQuery>;

export const contactSearchForm = t.type({
  company_name: t.string,
  principal_name: t.string,
});

export type ContactSearchForm = t.TypeOf<typeof contactSearchForm>;

export const contactOrderingQuery = t.type({
  ordering: t.string,
});

export const contactForm = t.type({
  company_name: requiredStringField(contactStrings.company_name),
  english_name: requiredStringField(contactStrings.english_name),
  zip_code: requiredStringField(contactStrings.zip_code),
  address1: requiredStringField(contactStrings.address1),
  address1_cn: stringField(contactStrings.city_name_cn),
  address2: stringField(contactStrings.address2),
  principal_name: requiredStringField(contactStrings.principal_name),
  phone: requiredStringField(contactStrings.phone),
  email: optionalEmailField(contactStrings.email),
  sns_id: stringField(contactStrings.sns_id),
  user: h.omittable(t.number),
  social_credit_id: stringField(contactStrings.social_credit_id).matches(
    /^$|^[0-9A-HJ-NPQRTUWXY]{2}\d{6}[0-9A-HJ-NPQRTUWXY]{10}$/,
    contactStrings.usciFormatError,
  ),
});

export const contactFormCN = t.type({
  ...contactForm.props,
  company_name: stringField(contactStrings.company_name),
  address1: requiredStringField(contactStrings.city_name),
  address2: stringField(contactStrings.address),
});

export type ContactForm = t.TypeOf<typeof contactForm>;

export const contactAdminCargosForm = t.type({
  ...contactForm.props,
  id: t.number,
  editing: t.boolean,
});

export type ContactAdminCargosForm = t.TypeOf<typeof contactAdminCargosForm>;

export const contactFormLoose = t.type({
  company_name: t.string,
  english_name: t.string,
  zip_code: t.string,
  address1: t.string,
  address1_cn: t.string,
  address2: t.string,
  principal_name: t.string,
  phone: t.string,
  email: t.string,
  sns_id: t.string,
  social_credit_id: t.string,
  user: h.omittable(t.number),
});

export type ContactFormLoose = t.TypeOf<typeof contactFormLoose>;

export const contactFormInitialValues: ContactForm = {
  company_name: '',
  english_name: '',
  zip_code: '',
  address1: '',
  address1_cn: '',
  address2: '',
  principal_name: '',
  phone: '',
  email: '',
  sns_id: '',
  user: undefined,
  social_credit_id: '',
};

export const mapContactToContactForm = (contact: Contact): ContactForm => {
  return {
    company_name: contact.company_name,
    english_name: contact.english_name,
    zip_code: contact.zip_code,
    address1: contact.address1,
    address2: contact.address2,
    address1_cn: contact.address1_cn,
    principal_name: contact.principal_name,
    phone: contact.phone,
    email: contact.email,
    sns_id: contact.sns_id,
    user: contact.user,
    social_credit_id: contact.social_credit_id,
  };
};

export const areContactsEqual = (a: ContactForm, b: ContactForm) => {
  const keys: (keyof ContactForm)[] = [
    'company_name',
    'english_name',
    'zip_code',
    'address1',
    'address2',
    'principal_name',
    'phone',
    'email',
    'sns_id',
  ];

  return isEqual(pick(a, keys), pick(b, keys));
};

export const ownedContactForm = h.taggedUnion([
  {
    tag: t.literal('SAME_AS_OWNER'),
  },
  {
    tag: t.literal('DEFAULT'),
    ...contactForm.props,
  },
]);

export type OwnedContactForm = t.TypeOf<typeof ownedContactForm>;

export const ownedContactFormCN = h.taggedUnion([
  {
    tag: t.literal('SAME_AS_OWNER'),
  },
  {
    tag: t.literal('DEFAULT'),
    ...contactFormCN.props,
  },
]);

export const contactAddFormRequireReceiver = t.type({
  contact: contactForm,
  ownedContacts: h.array(ownedContactForm).min(1, contactStrings.receiverRequired),
});

export const contactAddForm = t.type({
  contact: contactForm,
  ownedContacts: h.array(ownedContactForm),
});

export type ContactAddForm = t.TypeOf<typeof contactAddForm>;

export const contactAddFormCN = t.type({
  contact: contactFormCN,
  ownedContacts: h.array(ownedContactFormCN),
});

export const contactAddFormInitialValues: ContactAddForm = {
  contact: contactFormInitialValues,
  ownedContacts: [],
};

export const contactAction = t.union([
  t.literal('edit'),
  t.literal('createSuccess'),
  t.literal('editSuccess'),
  t.literal('editWithScrollBottom'),
  t.literal('createOwnedContact'),
]);

export type ContactAction = t.TypeOf<typeof contactAction>;

export type ContactRequestType = Exclude<ContactType, 'pickup_address' | 'receiver'>;

export const inviteForm = t.type({
  email: requiredStringField(contactStrings.email),
});

export const requestListExample = (type: ContactRequestType) =>
  ({
    shipper: [
      {
        company_name: '株式会社shipper',
        zip_code: '1100016',
        address1: '東京都台東区台東１−１５−６',
        address2: '903',
        principal_name: '豊臣秀吉',
        phone: '090-1145-1414',
        email: 'xiabeize@qq.com',
        sns_id: 'tuzi',
        contact_type: 'shipper',
        owner: 1,
        id: 1,
        use_count: 1,
        updated_at: '2021-10-08T22:19:33.423860+09:00',
        created_at: '2021-10-08T22:19:33.423860+09:00',
        user: 1,
      },
      {
        company_name: '株式会社shipper',
        zip_code: '1100016',
        address1: '東京都台東区台東１−１５−６',
        address2: '903',
        principal_name: '豊臣秀吉',
        phone: '090-1145-1414',
        email: 'xiabeize@qq.com',
        sns_id: 'tuzi',
        contact_type: 'shipper',
        owner: 1,
        id: 2,
        use_count: 1,
        updated_at: '2021-10-08T22:19:33.423860+09:00',
        created_at: '2021-10-08T22:19:33.423860+09:00',
        user: 1,
      },
    ],
    consignee: [
      {
        company_name: '株式会社consignee',
        zip_code: '1100016',
        address1: '東京都台東区台東１−１５−６',
        address2: '903',
        principal_name: '豊臣秀吉',
        phone: '090-1145-1414',
        email: 'xiabeize@qq.com',
        sns_id: 'tuzi',
        contact_type: 'consignee',
        owner: 1,
        id: 3,
        use_count: 1,
        updated_at: '2021-10-08T22:19:33.423860+09:00',
        created_at: '2021-10-08T22:19:33.423860+09:00',
        user: 1,
      },
      {
        company_name: '株式会社consignee',
        zip_code: '1100016',
        address1: '東京都台東区台東１−１５−６',
        address2: '903',
        principal_name: '豊臣秀吉',
        phone: '090-1145-1414',
        email: 'xiabeize@qq.com',
        sns_id: 'tuzi',
        contact_type: 'consignee',
        owner: 1,
        id: 4,
        use_count: 1,
        updated_at: '2021-10-08T22:19:33.423860+09:00',
        created_at: '2021-10-08T22:19:33.423860+09:00',
        user: 1,
      },
    ],
  }[type]);

export const createValidateContactForm = (contactType: ContactType) => (values: any) => {
  const isCN = ['pickup_address', 'shipper'].includes(contactType);

  const errors = createValidate(isCN ? contactFormCN : contactForm)(values);

  if (isCN) {
    if (!validateEnglish(values.address1)) {
      errors.address1 = formStrings.englishPrompt;
    }
  }

  return errors;
};

export const createValidateContactAddForm = (contactType: ContactType) => (values: any) => {
  const isCN = ['pickup_address', 'shipper'].includes(contactType);

  const errors = createValidate(isCN ? contactAddFormCN : contactAddForm)(values);

  if (isCN) {
    if (!validateEnglish(values.contact.address1)) {
      errors.contact.address1 = formStrings.englishPrompt;
    }
    values.ownedContacts?.forEach((contact: any, index: number) => {
      if (!validateEnglish(contact.address1)) {
        errors.ownedContacts[index].address1 = formStrings.englishPrompt;
      }
    });
  }

  return errors;
};
